import { makeStyles } from '@fluentui/react-components';
import { useState } from 'react';

import { useAsync } from '../common';
import { SearchBox, SectionLabel } from '../components';
import { useCurrentUser } from '../current-user';
import { palette, tokens } from '../theme';
import { listParticipationWorkItems, useWorkItemSearch, type WorkItem, WorkItemResultRow } from '../work-items';

export type QuickAddSectionProps = {
    date: string;
    selected: WorkItem | null;
    onSelect: (item: WorkItem) => void;
};

export const QuickAddSection: React.FC<QuickAddSectionProps> = ({ date, selected, onSelect }) => {
    const classes = useStyles();
    const { user } = useCurrentUser();
    const teamMemberId = user?.id ?? '';
    const year = Number(date.slice(0, 4));
    const month = Number(date.slice(5, 7));

    const [query, setQuery] = useState('');
    const searching = query.trim().length > 0;
    const { results, loading: searchInFlight } = useWorkItemSearch(query, 200);

    // With no query typed, open with the same iteration items the backfill
    // dialog suggests; the search box narrows to a title match once used.
    const defaults = useAsync(
        () => (teamMemberId ? listParticipationWorkItems(teamMemberId, year, month) : Promise.resolve([] as WorkItem[])),
        [teamMemberId, year, month],
    );

    const items = searching ? results : defaults.status === 'success' ? defaults.data : [];
    const loading = searching ? searchInFlight : defaults.status === 'loading';

    return (
        <div className={classes.root}>
            <SectionLabel text="Quick add" />

            <SearchBox value={query} onChange={setQuery} />

            <div className={classes.results}>
                {loading ? (
                    <p className={classes.searchingText}>{searching ? 'Searching…' : 'Loading…'}</p>
                ) : items.length === 0 ? null : (
                    <div className={classes.resultsScroll}>
                        {items.map((item) => (
                            <WorkItemResultRow key={item.id} item={item} selected={selected?.id === item.id} onSelect={onSelect} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        flexGrow: 1,
    },
    results: {
        flexGrow: 1,
        minHeight: 0,
        marginTop: tokens.spacingVerticalS,
    },
    resultsScroll: {
        height: '100%',
        overflowY: 'auto',
        paddingRight: tokens.spacingHorizontalS,
    },
    searchingText: {
        margin: `${tokens.spacingVerticalS} 0 0 0`,
        fontSize: '12px',
        color: palette.dimText,
    },
});
